import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScanFace, Loader2, Download, Check, FileText, ArrowLeft, CreditCard, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Resultado() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id");
  const [phase, setPhase] = useState("checking"); // checking | awaiting_payment | generating | done | error
  const [analysis, setAnalysis] = useState(null);
  const [afters, setAfters] = useState([]);
  const [checkoutUrl, setCheckoutUrl] = useState("https://pay.kiwify.com.br/ENBDhcH");
  const started = useRef(false);
  const generating = useRef(false);

  useEffect(() => {
    if (!orderId || started.current) return;
    started.current = true;

    const generate = async () => {
      if (generating.current) return;
      generating.current = true;
      setPhase("generating");
      try {
        await axios.post(`${API}/orders/${orderId}/generate`);
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        setAnalysis(data.analysis);
        setAfters(data.after_images || []);
        setPhase("done");
      } catch (err) {
        console.error("Erro ao gerar estudo:", err);
        setPhase("error");
        toast.error("Falha ao gerar o estudo.");
      }
    };

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        if (data.payment_status === "paid") {
          if (data.generation_status === "done") {
            setAnalysis(data.analysis);
            setAfters(data.after_images || []);
            setPhase("done");
          } else {
            generate();
          }
          return;
        }
        setPhase("awaiting_payment");
      } catch (err) {
        console.error("Erro ao verificar pedido:", err);
      }
      setTimeout(poll, 4000);
    };

    axios.get(`${API}/config`).then(({ data }) => data?.checkout_url && setCheckoutUrl(data.checkout_url)).catch(() => {});
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const download = () => {
    window.open(`${API}/orders/${orderId}/pdf`, "_blank");
  };

  if (!orderId) {
    return <Center><p className="text-[#F5F1E8]/70">Pedido inválido.</p><Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-[#C8A46A] text-[#0F0F10]">Voltar ao início</Button></Center>;
  }

  return (
    <div className="min-h-screen bg-[#0F0F10] text-[#F5F1E8]">
      <header className="border-b border-white/10 bg-[#0F0F10]/85 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-serif text-lg"><img src="/logo.png" alt="Visagismo IA" className="w-8 h-8 rounded-full object-cover ring-1 ring-[#C8A46A]/40" /> Visagismo<span className="text-[#C8A46A]"> IA</span></div>
          <button onClick={() => navigate("/")} data-testid="home-btn" className="text-sm text-[#F5F1E8]/70 hover:text-[#C8A46A] flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Início</button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16">
        {(phase === "checking" || phase === "generating") && (
          <div className="text-center py-20" data-testid="loading-state">
            <div className="relative w-20 h-20 mx-auto mb-8">
              <Loader2 className="w-20 h-20 animate-spin text-[#C8A46A]/30" />
              <ScanFace className="w-8 h-8 text-[#C8A46A] absolute inset-0 m-auto" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl">
              {phase === "checking" ? "Preparando seu pedido…" : "Gerando seu Estudo Profissional de Visagismo…"}
            </h1>
            <p className="text-[#F5F1E8]/70 mt-3">{phase === "checking" ? "Só um instante." : "Nossa IA está analisando seu rosto e criando suas visualizações — isso pode levar um minuto."}</p>
          </div>
        )}

        {phase === "awaiting_payment" && (
          <div className="text-center py-16" data-testid="awaiting-payment-state">
            <div className="w-16 h-16 rounded-full bg-[#C8A46A]/15 text-[#C8A46A] flex items-center justify-center mx-auto mb-6"><CreditCard className="w-8 h-8" /></div>
            <h1 className="font-serif text-3xl md:text-4xl">Falta só o pagamento</h1>
            <p className="text-[#F5F1E8]/70 mt-3 max-w-md mx-auto">Conclua o pagamento com segurança na Kiwify usando o <strong className="text-[#F5F1E8]">mesmo e-mail</strong> informado. Assim que confirmarmos, seu estudo é gerado automaticamente aqui.</p>
            <a href={checkoutUrl} target="_blank" rel="noopener noreferrer" data-testid="kiwify-pay-btn">
              <Button size="lg" className="mt-7 rounded-full bg-[#C8A46A] text-[#0F0F10] hover:bg-[#d9b884] h-14 px-8 text-base font-semibold">
                <CreditCard className="w-5 h-5 mr-2" /> Ir para o pagamento
              </Button>
            </a>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#F5F1E8]/50">
              <Clock className="w-4 h-4 animate-pulse" /> Aguardando confirmação do pagamento…
            </div>
            <p className="text-xs text-[#F5F1E8]/50 mt-4">Já pagou? Deixe esta página aberta — ela libera sozinha em instantes.</p>
          </div>
        )}

        {phase === "error" && (
          <div className="text-center py-20" data-testid="error-state">
            <h1 className="font-serif text-3xl">Algo deu errado</h1>
            <p className="text-[#F5F1E8]/70 mt-3">Se o pagamento foi concluído, tente recarregar a página.</p>
            <div className="flex gap-3 justify-center mt-6">
              <Button onClick={() => window.location.reload()} data-testid="retry-btn" className="rounded-full bg-[#C8A46A] text-[#0F0F10] hover:bg-[#d9b884]">Tentar novamente</Button>
              <Button onClick={() => navigate("/")} variant="outline" className="rounded-full border-white/20 text-[#F5F1E8] hover:bg-white/5">Início</Button>
            </div>
          </div>
        )}

        {phase === "done" && analysis && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} data-testid="done-state">
            <div className="text-center mb-8">
              <div className="w-14 h-14 rounded-full bg-[#C8A46A] text-[#0F0F10] flex items-center justify-center mx-auto mb-4"><Check className="w-7 h-7" /></div>
              <h1 className="font-serif text-4xl">Seu Estudo Profissional de Visagismo está pronto!</h1>
              <p className="text-[#F5F1E8]/70 mt-2">Confira o resumo abaixo e baixe o PDF completo.</p>
            </div>

            <Button onClick={download} data-testid="download-pdf-btn" size="lg" className="w-full rounded-full bg-[#C8A46A] text-[#0F0F10] hover:bg-[#d9b884] h-14 text-base mb-8 font-semibold">
              <Download className="w-5 h-5 mr-2" /> Baixar meu PDF
            </Button>

            {afters.length > 0 && (
              <div className="mb-8">
                <span className="text-xs tracking-[0.2em] uppercase text-[#C8A46A] font-semibold">Visualização do resultado — mantendo o seu rosto</span>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
                  {afters.map((af, i) => (
                    <div key={`after-${i}`} data-testid={`after-image-${i}`} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.03]">
                      <img src={`data:image/png;base64,${af.image}`} alt={af.titulo || `Resultado ${i + 1}`} className="w-full aspect-[4/5] object-cover" />
                      <p className="text-xs text-center text-[#F5F1E8]/70 font-medium py-2 px-1">{af.titulo || `Opção ${i + 1}`}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mb-8">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <span className="text-xs tracking-[0.2em] uppercase text-[#C8A46A] font-semibold">Formato do rosto</span>
                <p className="font-serif text-3xl mt-1 capitalize">{analysis.formato_rosto}</p>
                <p className="text-[#F5F1E8]/70 text-sm mt-3 leading-relaxed">{analysis.analise_facial}</p>
              </div>
            </div>

            <div className="space-y-5">
              <Section title="Cortes recomendados" items={analysis.cortes_recomendados} />
              <Section title="O que evitar" items={analysis.cortes_evitar} />
              {analysis.instrucoes_profissional && (
                <div className="rounded-2xl border border-[#C8A46A]/40 bg-white/[0.03] p-6">
                  <div className="flex items-center gap-2 mb-2"><FileText className="w-4 h-4 text-[#C8A46A]" /><span className="text-xs tracking-[0.2em] uppercase text-[#C8A46A] font-semibold">Leve ao seu profissional</span></div>
                  <p className="text-[#F5F1E8]/70 leading-relaxed">{analysis.instrucoes_profissional}</p>
                </div>
              )}
            </div>

            <Button onClick={download} data-testid="download-pdf-btn-2" size="lg" className="w-full rounded-full bg-[#C8A46A] text-[#0F0F10] hover:bg-[#d9b884] h-14 text-base mt-8 font-semibold">
              <Download className="w-5 h-5 mr-2" /> Baixar PDF completo
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
}

const Section = ({ title, items }) => (
  Array.isArray(items) && items.length ? (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
      <span className="text-xs tracking-[0.2em] uppercase text-[#C8A46A] font-semibold">{title}</span>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => <li key={`${title}-${i}`} className="flex gap-2 text-[#F5F1E8]/70"><Check className="w-4 h-4 text-[#C8A46A] shrink-0 mt-1" /> <span>{it}</span></li>)}
      </ul>
    </div>
  ) : null
);

const Center = ({ children }) => (
  <div className="min-h-screen bg-[#0F0F10] text-[#F5F1E8] flex flex-col items-center justify-center px-6 text-center">{children}</div>
);
